import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Badge, Button, Card } from 'react-bootstrap';
import { FaArrowLeft, FaHeart, FaStar, FaMapMarkerAlt, FaChartLine, FaArrowRight } from 'react-icons/fa';
import '../../../assets/css/student/blogs/StoriesDetail.css';
import MyNavbar from "../../../components/student/mynavbar/MyNavbar";

const storiesData = [
  {
    id: 1,
    title: "From Struggle to Success: Fatima's Medical Dream",
    excerpt: "How a girl from a small village overcame poverty to become a doctor and now serves her community with pride.",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80",
    impact: "Helped 500+ patients",
    location: "Lahore, Pakistan",
    category: "Medicine",
    year: "2023",
    fullStory: `Fatima grew up in a small village near Lahore where electricity was a luxury and books were rare. Her father worked as a daily-wage laborer, and her mother managed the household. Despite all odds, Fatima's curiosity and passion for learning never dimmed.

At the age of 12, she walked 5 kilometers every day to reach the nearest school. Her teachers recognized her brilliance and encouraged her to dream bigger. When she passed her matriculation exams with top marks, the entire village celebrated — but the real challenge was yet to come.

Medical school fees were beyond her family's means. That's when our scholarship program stepped in. With full financial support, Fatima enrolled in King Edward Medical University, one of Pakistan's most prestigious institutions.

She graduated with honors in 2021 and returned to her village to open a small clinic. Today, she has treated over 500 patients — many of whom had never seen a doctor before. Fatima also runs free health camps every month, reaching even more remote communities.

"Education didn't just change my life," she says. "It gave me the power to change others." Her story is a testament to what becomes possible when a deserving student gets the support they need.`,
    quote: "Education didn't just change my life — it gave me the power to change others.",
    donorNote: "Funded by the Al-Noor Education Trust and 12 individual donors.",
    stats: [
      { label: "Patients Treated", value: "500+" },
      { label: "Free Camps", value: "24" },
      { label: "Years Supported", value: "5" },
      { label: "Lives Impacted", value: "1,200+" }
    ]
  },
  {
    id: 2,
    title: "The Teacher Who Never Gave Up: Usman's Story",
    excerpt: "A young man's journey from being unable to afford school fees to becoming an inspiring teacher for underprivileged children.",
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80",
    impact: "Teaching 200+ students",
    location: "Karachi, Pakistan",
    category: "Education",
    year: "2022",
    fullStory: `Usman was the eldest of six siblings in a Karachi slum. His parents ran a small tea stall, barely making enough to feed the family. Despite poverty, Usman had an unquenchable thirst for knowledge and a natural gift for explaining things to others.

When his family could no longer afford his school fees, a neighbor told them about our scholarship program. Within weeks, Usman was enrolled and fully funded through secondary school and then a B.Ed program.

He graduated as a certified teacher and returned to his neighborhood — not to escape it, but to transform it. He started teaching in a community school, often staying late to help students who struggled. He introduced innovative methods, games, and storytelling to make learning fun.

Today, Usman teaches over 200 students across two shifts. He has also trained 15 volunteer tutors from the local university, multiplying his impact far beyond what one person could achieve alone.

"I know what it feels like to be told you can't go to school," Usman says. "That feeling drives me every single day."`,
    quote: "I know what it feels like to be told you can't go to school. That feeling drives me every single day.",
    donorNote: "Supported through the Karachi Education Fund.",
    stats: [
      { label: "Students Taught", value: "200+" },
      { label: "Volunteer Tutors Trained", value: "15" },
      { label: "Years Teaching", value: "3" },
      { label: "Pass Rate", value: "94%" }
    ]
  },
  {
    id: 3,
    title: "Engineering Hope: Ali's Transformation",
    excerpt: "From living in a one-room house to building tech solutions that help thousands, Ali's story inspires us all.",
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80",
    impact: "Created 50+ jobs",
    location: "Islamabad, Pakistan",
    category: "Technology",
    year: "2023",
    fullStory: `Ali and his family of seven lived in a single room in Rawalpindi. His father drove a rickshaw; his mother took in sewing work. But Ali had a mind for machines — he could fix anything and loved to tinker with old electronics.

A science teacher noticed Ali's talent and helped him apply for a scholarship to study Computer Engineering at FAST University. With funding secured, Ali threw himself into his studies.

During his final year, he built a low-cost irrigation monitoring system using basic microcontrollers and SMS alerts — a tool that small farmers could actually afford. His project won a national innovation award and caught the eye of investors.

After graduating, Ali launched a tech startup focused on agri-tech solutions. The company now employs over 50 people, most of them young graduates from underserved backgrounds. His software is used by thousands of small farmers across Punjab and Sindh.

"I always believed technology should solve real problems for real people," Ali says. "My background taught me what those problems look like."`,
    quote: "I always believed technology should solve real problems for real people. My background taught me what those problems look like.",
    donorNote: "Scholarship funded by the Pakistan Tech Development Foundation.",
    stats: [
      { label: "Jobs Created", value: "50+" },
      { label: "Farmers Helped", value: "3,000+" },
      { label: "Awards Won", value: "4" },
      { label: "Startup Valuation", value: "PKR 12M" }
    ]
  },
  {
    id: 4,
    title: "Breaking Boundaries: Ayesha's Business Success",
    excerpt: "How education empowered a young woman to start her own business and employ other women from her community.",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80",
    impact: "Employed 30+ women",
    location: "Multan, Pakistan",
    category: "Entrepreneurship",
    year: "2022",
    fullStory: `In Multan, girls were often told that education beyond primary school was unnecessary. Ayesha refused to accept that. She convinced her parents to let her continue studying, and when financial barriers appeared, a scholarship made the difference.

She pursued a degree in Business Administration and graduated top of her class. Armed with knowledge and determination, she returned home not to a job — but to create jobs.

Ayesha started a handcrafted textile business celebrating Multan's famous blue pottery and embroidery traditions. She hired women from her neighborhood, trained them in quality control and business basics, and helped them earn incomes for the first time.

Today, her business exports to three countries and employs over 30 women — all from low-income families. She also mentors young women aspiring to start businesses of their own.

"When a woman earns, a whole family rises," Ayesha says. "I just wanted to be the reason a few families could rise."`,
    quote: "When a woman earns, a whole family rises. I just wanted to be the reason a few families could rise.",
    donorNote: "Funded by Women Empower Pakistan and the Multan Community Trust.",
    stats: [
      { label: "Women Employed", value: "30+" },
      { label: "Export Countries", value: "3" },
      { label: "Mentees", value: "45" },
      { label: "Annual Revenue", value: "PKR 8M" }
    ]
  },
  {
    id: 5,
    title: "The Scientist's Journey: Hassan's Research",
    excerpt: "From struggling to afford textbooks to conducting groundbreaking research in renewable energy.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80",
    impact: "3 Published papers",
    location: "Faisalabad, Pakistan",
    category: "Science",
    year: "2023",
    fullStory: `Hassan grew up in a modest household in Faisalabad where his father worked at a textile mill. He always loved science but could barely afford secondhand textbooks, let alone a university education.

A scholarship changed everything. Hassan enrolled in the University of Engineering and Technology, Lahore, and immediately stood out for his dedication. He spent long hours in the library and labs, often sleeping just a few hours to make the most of every day.

His focus became solar energy storage — a critical challenge for Pakistan's power-stricken population. He developed a cost-effective battery prototype using locally available materials, and his research was published in three international journals.

He is now pursuing a PhD in Germany on a fully funded fellowship, continuing research that could one day power thousands of rural homes in Pakistan.

"Every late night in that lab, I thought of my father coming home exhausted from the mill," Hassan says. "I wanted to find a way so others didn't have to live without reliable power."`,
    quote: "Every late night in that lab, I thought of my father coming home exhausted. I wanted to find a way so others didn't have to live without reliable power.",
    donorNote: "Supported by the National Science Scholarship Program.",
    stats: [
      { label: "Papers Published", value: "3" },
      { label: "Citations", value: "80+" },
      { label: "Fellowships Awarded", value: "2" },
      { label: "Prototype Cost Reduction", value: "60%" }
    ]
  },
  {
    id: 6,
    title: "Artist with a Purpose: Zainab's Creative Path",
    excerpt: "How art education transformed a shy girl into a celebrated artist who now teaches art to underprivileged children.",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&q=80",
    impact: "Trained 100+ students",
    location: "Peshawar, Pakistan",
    category: "Arts",
    year: "2021",
    fullStory: `Zainab was so quiet in school that her teachers often forgot she was there. But on paper, she was loud — her drawings told stories of her world with vivid emotion and detail. An art teacher saw her talent and helped her apply for a scholarship to the National College of Arts.

At NCA, Zainab blossomed. She explored painting, printmaking, and digital art. She graduated with a distinction and held her first exhibition in Lahore, where all her works sold within hours.

But fame and money were never her goals. Zainab returned to Peshawar and started free art classes in her neighborhood. She believed that art could heal, express, and empower — especially for children who had experienced trauma in conflict-affected regions.

Today, she has trained over 100 young students. Several of them have won provincial art competitions. A few have gone on to pursue art professionally, inspired by their teacher who once stood in their shoes.

"Art is not a luxury," Zainab says. "It's how we make sense of the world — and every child deserves that."`,
    quote: "Art is not a luxury. It's how we make sense of the world — and every child deserves that.",
    donorNote: "Funded through the Creative Pakistan Initiative.",
    stats: [
      { label: "Students Trained", value: "100+" },
      { label: "Exhibitions Held", value: "6" },
      { label: "Awards Won", value: "8" },
      { label: "Pro Artists Mentored", value: "12" }
    ]
  }
];

const StoryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const storyId = parseInt(id);
  const story = storiesData.find((s) => s.id === storyId);

  const prevStory = storiesData.find((s) => s.id === storyId - 1);
  const nextStory = storiesData.find((s) => s.id === storyId + 1);

  if (!story) {
    return (
      <div className="story-not-found">
        <Container className="text-center py-5">
          <h2>Story not found</h2>
          <Link to="/stories" className="btn btn-primary mt-3">Back to Stories</Link>
        </Container>
      </div>
    );
  }

  return (
    <div className="story-detail-page">
      {/* Hero Section */}
        <MyNavbar />
      <div
        className="story-detail-hero"
        style={{ backgroundImage: `url(${story.image})` }}
      >
        <div className="story-detail-hero-overlay">
          <Container>
            <Link to="/stories" className="story-back-btn">
              <FaArrowLeft /> Back to Stories
            </Link>

            <div className="story-detail-hero-content">
              <Badge className="story-category-badge">
                <FaStar className="me-1" /> {story.category}
              </Badge>
              <h1 className="story-detail-title">{story.title}</h1>
              <div className="story-detail-meta">
                <span className="meta-item">
                  <FaMapMarkerAlt className="me-1" /> {story.location}
                </span>
                <span className="meta-divider">|</span>
                <span className="meta-item">
                  <FaChartLine className="me-1" /> {story.impact}
                </span>
                <span className="meta-divider">|</span>
                <span className="meta-item">{story.year}</span>
              </div>
            </div>
          </Container>
        </div>
      </div>

      {/* Main Content */}
      <Container className="story-detail-container py-5">
        <Row className="g-5">

          {/* Story Body */}
          <Col lg={8}>
            <div className="story-body-card">
              <p className="story-lead">{story.excerpt}</p>
              <hr className="story-divider" />
              {story.fullStory.split('\n\n').map((para, i) => (
                <p key={i} className="story-paragraph">{para}</p>
              ))}

              {/* Quote Block */}
              <blockquote className="story-quote-block">
                <FaHeart className="quote-icon" />
                <p>"{story.quote}"</p>
                <footer>— {story.title.split(':')[0]}</footer>
              </blockquote>

              <div className="donor-note">
                <strong>Donor Note:</strong> {story.donorNote}
              </div>
            </div>
          </Col>

          {/* Sidebar Stats */}
          <Col lg={4}>
            <Card className="story-stats-card">
              <Card.Body>
                <h5 className="stats-heading">Impact at a Glance</h5>
                <Row className="g-3 mt-1">
                  {story.stats.map((stat, i) => (
                    <Col xs={6} key={i}>
                      <div className="stat-box">
                        <div className="stat-value">{stat.value}</div>
                        <div className="stat-label">{stat.label}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>

            <Card className="story-donate-card mt-4">
              <Card.Body className="text-center">
                <FaHeart className="donate-heart-icon" />
                <h5>Support More Stories Like This</h5>
                <p>Your donation helps deserving students reach their full potential.</p>
                <Button className="donate-btn w-100">Donate Now</Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Navigation Between Stories */}
        <div className="story-navigation mt-5">
          <div className="nav-prev">
            {prevStory && (
              <Link to={`/stories/${prevStory.id}`} className="story-nav-link">
                <FaArrowLeft />
                <div>
                  <span className="nav-label">Previous Story</span>
                  <span className="nav-title">{prevStory.title}</span>
                </div>
              </Link>
            )}
          </div>
          <div className="nav-next">
            {nextStory && (
              <Link to={`/stories/${nextStory.id}`} className="story-nav-link next">
                <div>
                  <span className="nav-label">Next Story</span>
                  <span className="nav-title">{nextStory.title}</span>
                </div>
                <FaArrowRight />
              </Link>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default StoryDetailPage;